/**
 * RBAC Authorization Engine
 * 
 * Centralized authorization logic that determines if a user
 * with given roles has the required permissions.
 * 
 * This is the single source of truth for all authorization checks.
 */

import { Permission } from './permissions';
import { Role } from './roles';
import { getPermissionsForRoles } from './rolePermissionMap';

/**
 * Authorization result
 */
export interface AuthorizationResult {
  authorized: boolean;
  reason?: string;
}

/**
 * Check if user has a specific permission
 * 
 * @param userRoles - Array of user roles
 * @param requiredPermission - Permission to check
 * @returns true if user has the permission, false otherwise
 */
export const hasPermission = (
  userRoles: (Role | string)[],
  requiredPermission: Permission | string
): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false; // Deny by default
  }

  const userPermissions = getPermissionsForRoles(userRoles);
  return userPermissions.includes(requiredPermission as Permission);
};

/**
 * Check if user has any of the required permissions
 * 
 * @param userRoles - Array of user roles
 * @param requiredPermissions - Array of permissions (OR logic)
 * @returns true if user has at least one permission, false otherwise
 */
export const hasAnyPermission = (
  userRoles: (Role | string)[],
  requiredPermissions: (Permission | string)[]
): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false; // Deny by default
  }

  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No requirements means allowed
  }

  const userPermissions = getPermissionsForRoles(userRoles);
  return requiredPermissions.some(permission => 
    userPermissions.includes(permission as Permission)
  );
};

/**
 * Check if user has all of the required permissions
 * 
 * @param userRoles - Array of user roles
 * @param requiredPermissions - Array of permissions (AND logic)
 * @returns true if user has all permissions, false otherwise
 */
export const hasAllPermissions = (
  userRoles: (Role | string)[],
  requiredPermissions: (Permission | string)[]
): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false; // Deny by default
  }

  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No requirements means allowed
  }

  const userPermissions = getPermissionsForRoles(userRoles);
  return requiredPermissions.every(permission => 
    userPermissions.includes(permission as Permission)
  );
};

/**
 * Check if user has a specific role
 * 
 * Note: Prefer permission checks over role checks when possible.
 * This is provided for cases where role-based logic is necessary.
 * 
 * @param userRoles - Array of user roles
 * @param requiredRole - Role to check
 * @returns true if user has the role, false otherwise
 */
export const hasRole = (
  userRoles: (Role | string)[],
  requiredRole: Role | string
): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false; // Deny by default
  }

  return userRoles.includes(requiredRole);
};

/**
 * Check if user has any of the required roles
 * 
 * @param userRoles - Array of user roles
 * @param requiredRoles - Array of roles (OR logic)
 * @returns true if user has at least one role, false otherwise
 */
export const hasAnyRole = (
  userRoles: (Role | string)[],
  requiredRoles: (Role | string)[]
): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false; // Deny by default
  }

  if (!requiredRoles || requiredRoles.length === 0) {
    return true; // No requirements means allowed
  }

  return requiredRoles.some(role => userRoles.includes(role));
};

/**
 * Get detailed authorization result with reason
 * 
 * @param userRoles - Array of user roles
 * @param requiredPermission - Permission to check
 * @returns AuthorizationResult with authorized flag and optional reason
 */
export const checkAuthorization = (
  userRoles: (Role | string)[],
  requiredPermission: Permission | string
): AuthorizationResult => {
  const authorized = hasPermission(userRoles, requiredPermission);
  
  if (!authorized) {
    return {
      authorized: false,
      reason: `User does not have required permission: ${requiredPermission}`,
    };
  }

  return { authorized: true };
};

// Ensure this file is treated as a module
export {};

